import { useEffect, useState } from "react";
import { BellRing, Download, Share, X } from "lucide-react";
import { api } from "../lib/api";
import { Alert, Button, Card } from "./ui";

/** La web abierta como app (desde el icono de la pantalla de inicio), no en una pestaña del navegador. */
export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  (navigator as unknown as { standalone?: boolean }).standalone === true;
const isIos = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const pushSupported = () =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

function keyToBytes(base64url: string) {
  const b64 = (base64url + "=".repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

/** Registro del service worker (solo existe en la web publicada; en desarrollo no hay avisos push). */
const registration = () => navigator.serviceWorker.getRegistration();

async function currentSubscription() {
  const reg = await registration();
  return reg ? reg.pushManager.getSubscription() : null;
}

/**
 * Avisos push en este dispositivo (Web Push): recordatorios de fichaje, salidas automáticas, solicitudes…
 * En iPhone solo funcionan con WulfTime añadida a la pantalla de inicio (iOS 16.4 o posterior).
 */
export function DevicePush() {
  const [state, setState] = useState<
    | "loading"
    | "unsupported"
    | "ios-install"
    | "denied"
    | "off"
    | "on"
    | "unavailable"
  >("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!pushSupported())
        return setState(
          isIos() && !isStandalone() ? "ios-install" : "unsupported",
        );
      const cfg = await api
        .get<{ webPushPublicKey: string | null }>("/api/v1/push/config")
        .catch(() => ({ webPushPublicKey: null }));
      if (
        !cfg.webPushPublicKey ||
        !(await registration().catch(() => undefined))
      )
        return setState("unavailable");
      if (Notification.permission === "denied") return setState("denied");
      setState(
        (await currentSubscription().catch(() => null)) &&
          Notification.permission === "granted"
          ? "on"
          : "off",
      );
    })();
  }, []);

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const cfg = await api.get<{ webPushPublicKey: string }>(
        "/api/v1/push/config",
      );
      if ((await Notification.requestPermission()) !== "granted")
        return setState("denied");
      const reg = await registration();
      if (!reg)
        throw new Error(
          "La app aún se está preparando. Recarga la página e inténtalo de nuevo.",
        );
      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: keyToBytes(cfg.webPushPublicKey),
        }));
      await api.post("/api/v1/me/devices", {
        platform: "WEB",
        pushToken: JSON.stringify(sub.toJSON()),
        name: `${isIos() ? "iPhone/iPad" : /android/i.test(navigator.userAgent) ? "Android" : "Navegador"}${isStandalone() ? " (app)" : ""}`,
      });
      setState("on");
    } catch (e) {
      // Los errores del navegador vienen en inglés: se explican en castellano
      const name = (e as { name?: string })?.name;
      setError(
        name === "NotAllowedError"
          ? "El navegador no ha permitido los avisos. Revisa que las notificaciones estén permitidas para WulfTime (y, en iPhone, que la abras desde el icono de la pantalla de inicio)."
          : name === "AbortError" || name === "NotSupportedError"
            ? "Este navegador no ha podido registrarse para recibir avisos. Prueba con Chrome o Safari actualizados."
            : e instanceof Error && !/^[A-Za-z ,.-]+$/.test(e.message)
              ? e.message
              : "No se han podido activar los avisos. Inténtalo de nuevo.",
      );
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const sub = await currentSubscription();
      if (sub) {
        await api
          .post("/api/v1/me/devices/unregister", {
            pushToken: JSON.stringify(sub.toJSON()),
          })
          .catch(() => {});
        await sub.unsubscribe();
      }
      setState("off");
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading" || state === "unavailable") return null;
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <BellRing className="h-4 w-4" /> Avisos en este dispositivo
        </span>
      }
    >
      {state === "on" && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-600">
            Activados: recibirás aquí los avisos aunque WulfTime esté cerrada.
          </p>
          <Button
            size="sm"
            variant="secondary"
            loading={busy}
            onClick={disable}
          >
            Desactivar
          </Button>
        </div>
      )}
      {state === "off" && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-600">
            Recordatorios de fichaje, salidas automáticas, respuestas a tus
            solicitudes… aunque WulfTime esté cerrada.
          </p>
          <Button size="sm" loading={busy} onClick={enable}>
            Activar avisos
          </Button>
        </div>
      )}
      {state === "ios-install" && (
        <p className="text-sm text-slate-600">
          En iPhone, primero añade WulfTime a la pantalla de inicio (en Safari:
          botón Compartir → «Añadir a pantalla de inicio») y ábrela desde el
          icono. Después podrás activar los avisos aquí.
        </p>
      )}
      {state === "denied" && (
        <p className="text-sm text-slate-600">
          Los avisos están bloqueados para WulfTime en este dispositivo.
          Actívalos en los ajustes del navegador (o en Ajustes → Notificaciones
          → WulfTime en el iPhone) y vuelve aquí.
        </p>
      )}
      {state === "unsupported" && (
        <p className="text-sm text-slate-600">
          Este navegador no admite avisos. Prueba con Chrome, Edge, Firefox o
          Safari actualizados.
        </p>
      )}
      {error && (
        <Alert tone="error" className="mt-2">
          {error}
        </Alert>
      )}
    </Card>
  );
}

type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
};

/** Invitación a instalar WulfTime como app (Android/ordenador: botón; iPhone: cómo hacerlo). Se puede cerrar. */
export function InstallBanner() {
  const [prompt, setPrompt] = useState<InstallPrompt | null>(null);
  const [hidden, setHidden] = useState(() => {
    try {
      return (
        isStandalone() || localStorage.getItem("wt-install-dismissed") === "1"
      );
    } catch {
      return isStandalone();
    }
  });
  useEffect(() => {
    const on = (e: Event) => {
      e.preventDefault();
      setPrompt(e as InstallPrompt);
    };
    window.addEventListener("beforeinstallprompt", on);
    return () => window.removeEventListener("beforeinstallprompt", on);
  }, []);
  const dismiss = () => {
    setHidden(true);
    try {
      localStorage.setItem("wt-install-dismissed", "1");
    } catch {
      /* sin almacenamiento */
    }
  };
  if (hidden || (!prompt && !isIos()) || window.innerWidth > 1024) return null;
  return (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50 p-3 text-sm">
      {prompt ? (
        <Download className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
      ) : (
        <Share className="mt-0.5 h-4 w-4 shrink-0 text-brand-700" />
      )}
      <div className="flex-1">
        <p className="font-medium text-brand-900">
          Instala WulfTime en tu móvil
        </p>
        {prompt ? (
          <p className="text-slate-600">
            Se abre como una app, desde su icono, y puede avisarte aunque esté
            cerrada.
          </p>
        ) : (
          <p className="text-slate-600">
            En Safari pulsa <strong>Compartir</strong> y luego{" "}
            <strong>«Añadir a pantalla de inicio»</strong>. Se abrirá como una
            app y podrá avisarte.
          </p>
        )}
        {prompt && (
          <Button
            size="sm"
            className="mt-2"
            onClick={() =>
              prompt
                .prompt()
                .then(() => prompt.userChoice)
                .then(() => setPrompt(null))
            }
          >
            Instalar
          </Button>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Cerrar"
        className="rounded p-1 text-slate-400 hover:text-slate-700"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
